// src/utils/revueFnct.ts — SCE Issues
// La revue des FNC-T (lot 3 du § 14, piste B retenue le 22/10 ; maquette
// https://claude.ai/artifact/2nfLQyNb8UjEEBZs5VCmNm, artboard « Revue »). Pur.
//
// Quatre files — prêtes à clore, actions en retard, à échéance cette semaine,
// ouvertes depuis plus de 90 jours —, les actions ouvertes par responsable, ce
// qui a changé depuis la dernière revue (le point jaune), le baromètre de
// l'année. « Marquer la revue faite » note la date et le nom : bloc `revueFnct`
// de la ligne `__global__` de SCE_UserPrefs (24 revues gardées).
//
// Les relevés (pas encore ouverts) restent hors des files : ils n'ont ni actions
// ni ancienneté. Les actions d'une FNC-T close ne comptent plus.

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import { libelleCategorie } from '@/config/fnct.config'
import { cleNom, debutFnct, echeanceEnVigueur, estEnReleve, estTerminee, joursEntre, jourParis, memePersonne, statutAction } from './fnct'
import { pretAClore } from './ficheFnct'
import { finDeSemaine } from './mesFnct'
import { beAttendu } from './attenteBE'
import { nombreDeReports } from './actionsFnct'

// ─── La dernière revue ───

export const CLE_BLOC_REVUE_FNCT = 'revueFnct'
export const MAX_REVUES_GARDEES = 24

export interface RevueFaite {
  /** Date et heure, ISO 8601. */
  le: string
  /** « Prénom Nom » de qui l'a marquée. */
  par: string
}

export interface BlocRevueFnct {
  v: 1
  /** La plus récente en dernier. */
  revues: RevueFaite[]
}

/** Lecture tolérante : une revue sans date lisible est écartée. */
export function lireBlocRevue(x: unknown): RevueFaite[] {
  if (typeof x !== 'object' || x === null) return []
  const revues = (x as { revues?: unknown }).revues
  if (!Array.isArray(revues)) return []
  return revues
    .filter((r): r is RevueFaite => typeof r === 'object' && r !== null && typeof (r as RevueFaite).le === 'string' && !Number.isNaN(Date.parse((r as RevueFaite).le)))
    .map((r) => ({ le: r.le, par: typeof r.par === 'string' ? r.par : '' }))
    .sort((a, b) => a.le.localeCompare(b.le))
}

export function derniereRevue(x: unknown): RevueFaite | null {
  return lireBlocRevue(x).at(-1) ?? null
}

/** Le bloc après « Marquer la revue faite » (relu juste avant : rien n'est perdu). */
export function marquerRevue(x: unknown, par: string, maintenant: Date): BlocRevueFnct {
  return { v: 1, revues: [...lireBlocRevue(x), { le: maintenant.toISOString(), par }].slice(-MAX_REVUES_GARDEES) }
}

/** Le jour (heure de Paris) de la dernière revue : le point jaune part de là. */
export function jourDeRevue(r: RevueFaite | null): Jour | null {
  return r ? jourParis(new Date(r.le)) : null
}

// ─── Les files ───

export interface FnctPrete {
  fnct: Fnct
  /** Jour où la dernière action a été close : depuis quand tout est fait. */
  depuis: Jour | null
  risqueEvalue: boolean
  nouveau: boolean
}

export interface ActionRevue {
  action: ActionFnct
  fnct: Fnct
  echeance: Jour | null
  retard: number
  /** Combien de fois elle a été reportée (plusieurs reports permis depuis le 30/10). */
  reports: number
  /** Elle attend la mise à jour du BE (utils/attenteBE) : en retard quand même, mais on le dit. */
  attendBE: boolean
  nouveau: boolean
}

export interface FnctAncienne {
  fnct: Fnct
  age: number
  retards: number
  closes: number
  total: number
}

export interface LigneResponsable {
  nom: string
  /** Adresse lue sur l'une de ses actions : sa photo dans la pastille. */
  upn?: string
  retard: number
  semaine: number
  apres: number
}

export interface DepuisRevue {
  ouvertes: Fnct[]
  closes: Fnct[]
  actionsCloses: ActionFnct[]
}

export interface Revue {
  pretes: FnctPrete[]
  retards: ActionRevue[]
  semaine: ActionRevue[]
  anciennes: FnctAncienne[]
  responsables: LigneResponsable[]
  /** Null tant qu'aucune revue n'a été marquée. */
  depuis: DepuisRevue | null
  ouvertes: number
}

export const AGE_ANCIENNE = 90

/** Le nom de la ligne « Par responsable » des actions sans responsable. */
export const SANS_RESPONSABLE = '(sans responsable)'

/**
 * L'action est-elle à ce responsable de « Par responsable » ? Même clé que le
 * regroupement (casse, accents, espaces : `cleNom`) — un clic sur un nom montrait
 * jusque-là moins d'actions que sa ligne n'en comptait, et rien du tout pour
 * « (sans responsable) » (30/10).
 */
export function aCeResponsable(a: Pick<ActionFnct, 'responsable'>, nom: string): boolean {
  return cleNom(a.responsable.trim() || SANS_RESPONSABLE) === cleNom(nom)
}

export function calculerRevue(fncts: readonly Fnct[], actions: readonly ActionFnct[], aujourdhui: Jour, revue: Jour | null): Revue {
  const parFnct = new Map<string, ActionFnct[]>()
  for (const a of actions) {
    const k = a.fnct.toUpperCase()
    parFnct.set(k, [...(parFnct.get(k) ?? []), a])
  }
  const actives = fncts.filter((f) => !f.cloture && !estEnReleve(f))
  const finSemaine = finDeSemaine(aujourdhui)
  const pretes: FnctPrete[] = []
  const anciennes: FnctAncienne[] = []
  const retards: ActionRevue[] = []
  const semaine: ActionRevue[] = []
  const parNom = new Map<string, LigneResponsable>()

  for (const f of actives) {
    const siennes = parFnct.get(f.numero.toUpperCase()) ?? []
    const closesIci = siennes.filter((a) => a.cloture).map((a) => a.cloture as Jour)
    const derniere = closesIci.sort().at(-1) ?? null
    if (pretAClore(f, siennes)) {
      pretes.push({ fnct: f, depuis: derniere, risqueEvalue: f.severite !== null && f.frequence !== null, nouveau: !!revue && !!derniere && derniere > revue })
    }
    const debut = debutFnct(f)
    const age = debut ? joursEntre(debut, aujourdhui) : 0
    let enRetard = 0
    for (const a of siennes) {
      const statut = statutAction(a, aujourdhui)
      if (estTerminee(statut)) continue
      const echeance = echeanceEnVigueur(a)
      const nom = a.responsable.trim() || SANS_RESPONSABLE
      const k = cleNom(nom)
      const ligne: LigneResponsable = parNom.get(k) ?? { nom, retard: 0, semaine: 0, apres: 0 }
      if (!ligne.upn && a.responsableUPN) ligne.upn = a.responsableUPN
      const base = { action: a, fnct: f, echeance, reports: nombreDeReports(a), attendBE: !!beAttendu(a, f, siennes) }
      if (statut === 'en-retard') {
        enRetard += 1
        ligne.retard += 1
        // Nouveau : l'échéance est passée depuis la dernière revue.
        retards.push({ ...base, retard: echeance ? joursEntre(echeance, aujourdhui) : 0, nouveau: !!revue && !!echeance && echeance >= revue })
      } else if (echeance && echeance <= finSemaine) {
        ligne.semaine += 1
        semaine.push({ ...base, retard: 0, nouveau: false })
      } else ligne.apres += 1
      parNom.set(k, ligne)
    }
    if (age > AGE_ANCIENNE) anciennes.push({ fnct: f, age, retards: enRetard, closes: siennes.filter((a) => a.cloture).length, total: siennes.length })
  }

  pretes.sort((a, b) => (a.depuis ?? '').localeCompare(b.depuis ?? '') || a.fnct.numero.localeCompare(b.fnct.numero, 'fr', { numeric: true }))
  retards.sort((a, b) => b.retard - a.retard || a.action.id.localeCompare(b.action.id, 'fr', { numeric: true }))
  semaine.sort((a, b) => (a.echeance ?? '').localeCompare(b.echeance ?? '') || a.action.id.localeCompare(b.action.id, 'fr', { numeric: true }))
  anciennes.sort((a, b) => b.age - a.age)
  const responsables = [...parNom.values()].sort((a, b) => b.retard - a.retard || b.semaine - a.semaine || b.retard + b.semaine + b.apres - (a.retard + a.semaine + a.apres) || a.nom.localeCompare(b.nom, 'fr'))

  let depuis: DepuisRevue | null = null
  if (revue) {
    depuis = {
      ouvertes: fncts.filter((f) => !estEnReleve(f) && (debutFnct(f) ?? '') > revue).sort((a, b) => a.numero.localeCompare(b.numero, 'fr', { numeric: true })),
      closes: fncts.filter((f) => !!f.cloture && f.cloture > revue),
      actionsCloses: actions.filter((a) => !!a.cloture && a.cloture > revue),
    }
  }
  return { pretes, retards, semaine, anciennes, responsables, depuis, ouvertes: actives.length }
}

/** « FNC-T_2026-56, 57, 58 et 59 » : le N° complet une fois, puis les rangs de la même année. */
export function numerosCourts(fncts: readonly Pick<Fnct, 'numero' | 'annee' | 'rang'>[]): string {
  if (fncts.length === 0) return ''
  const morceaux = fncts.map((f, i) => (i > 0 && f.annee === fncts[i - 1]?.annee ? String(f.rang) : f.numero))
  return morceaux.length === 1 ? morceaux[0] ?? '' : `${morceaux.slice(0, -1).join(', ')} et ${morceaux.at(-1)}`
}

// ─── Réattribuer les actions d'une personne (lot 6 du § 14, 22/10) ───
//
// Quand quelqu'un part ou s'absente longtemps : l'historique ne bouge pas (les
// noms sont du texte), le travail ouvert change de mains, en un geste depuis la
// revue. Seules les actions ouvertes d'une FNC-T ouverte sont concernées.

export function actionsAReattribuer(fncts: readonly Fnct[], actions: readonly ActionFnct[], nom: string): ActionFnct[] {
  const closes = new Set(fncts.filter((f) => f.cloture).map((f) => f.numero.toUpperCase()))
  return actions.filter((a) => !a.cloture && !closes.has(a.fnct.toUpperCase()) && memePersonne(a.responsable, nom))
}

/** Historique « Action réattribuée » : la description, puis qui la portait et qui la porte. */
export function texteReattribution(a: Pick<ActionFnct, 'description'>, de: string, vers: string, motif = ''): string {
  return [a.description, `Responsable : ${de} → ${vers}`, motif.trim()].filter(Boolean).join('\n')
}

// ─── Le baromètre de l'année ───

export interface Barre {
  libelle: string
  n: number
}

export interface Barometre {
  annee: number
  total: number
  /** Janvier → mois en cours. */
  mois: Barre[]
  categories: Barre[]
  modeles: Barre[]
  /** Actions closes sur 12 mois glissants : combien à temps. */
  aTemps: { closes: number; aTemps: number }
}

const MOIS = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.']

function compter(valeurs: readonly string[], max: number, reste: string): Barre[] {
  const m = new Map<string, number>()
  for (const v of valeurs) m.set(v, (m.get(v) ?? 0) + 1)
  const tries = [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'fr')).map(([libelle, n]) => ({ libelle, n }))
  if (tries.length <= max) return tries
  const autres = tries.slice(max - 1).reduce((s, b) => s + b.n, 0)
  return [...tries.slice(0, max - 1), { libelle: reste, n: autres }]
}

export function barometre(fncts: readonly Fnct[], actions: readonly ActionFnct[], aujourdhui: Jour): Barometre {
  const annee = Number(aujourdhui.slice(0, 4))
  const moisCourant = Number(aujourdhui.slice(5, 7))
  const del = fncts.filter((f) => f.annee === annee)
  const mois = MOIS.slice(0, moisCourant).map((libelle, i) => ({
    libelle,
    n: del.filter((f) => Number((f.enregistrement ?? '').slice(5, 7)) === i + 1).length,
  }))
  const debut12 = `${annee - 1}${aujourdhui.slice(4)}`
  const closes = actions.filter((a) => !!a.cloture && (a.cloture as Jour) > debut12 && (a.cloture as Jour) <= aujourdhui)
  return {
    annee,
    total: del.length,
    mois,
    categories: compter(
      del.map((f) => (f.categorie ? libelleCategorie(f.categorie) : 'Non renseignée')),
      4,
      'Autres',
    ),
    modeles: compter(
      del.map((f) => f.modele || 'Non renseigné'),
      5,
      'Autres modèles',
    ),
    aTemps: { closes: closes.length, aTemps: closes.filter((a) => statutAction(a, aujourdhui) === 'terminee-a-temps').length },
  }
}
